import { useCallback } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  InformationalScreen,
  LoadingModal,
  Modal,
  Stack,
  Text,
} from '@adc/parallax-component-library';
import { useFormik } from 'formik';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import * as z from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';

import {
  Footer,
  FormContainer,
  MainContainer,
  PhoneInput,
  TopHeaderBackArrow,
} from '~/components/utility';
import { useRunOnceOnMountEffect } from '~/hooks';
import type { RootState } from '~/reducers';
import { buildTestId } from '~/utilities/buildTestId';
import i18n from '~/utilities/i18n';
import mediator from '~/utilities/mediator';
import { removeAuthSession } from '~/utilities/session';

const testID = 'create-account';

type FormValues = { phone: string };

const initialValues: FormValues = { phone: '' };

const mapStateToProps = ({
  nav: { query },
  config: { allowedCountries, checkAllowedCountries },
}: RootState) => ({
  allowedCountries,
  checkAllowedCountries,
  query,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const CreateAccount = ({
  allowedCountries,
  checkAllowedCountries,
  query,
}: Props) => {
  const validationSchema = z.object({
    phone: z
      .string(
        i18n.t(
          'CreateAccount.content.introForm.formField.phone.errors.required',
        ),
      )
      .min(
        1,
        i18n.t(
          'CreateAccount.content.introForm.formField.phone.errors.required',
        ),
      )
      .trim()
      .refine(
        (value) => parsePhoneNumberFromString(value)?.isPossible() ?? false,
        {
          error: i18n.t(
            'CreateAccount.content.introForm.formField.phone.errors.invalid',
          ),
        },
      )
      .refine(
        (value) => {
          if (!checkAllowedCountries) {
            return true;
          }

          const phoneNumber = parsePhoneNumberFromString(value);

          if (!phoneNumber) {
            return false;
          }

          return allowedCountries.includes(phoneNumber.country ?? 'US');
        },
        {
          error: i18n.t(
            'CreateAccount.content.introForm.formField.phone.errors.onlyContriesAllowed',
            {
              countries: allowedCountries.join(', '),
            },
          ),
        },
      ),
  });

  const handleGoBack = useCallback(() => {
    mediator.publish('router:back');
  }, []);

  const onSubmit = useCallback(
    ({ values }: { values: FormValues }) => {
      Modal.show({
        testID: buildTestId({ testID, suffix: ['confirm', 'modal'] }),
        header: i18n.t('CreateAccount.content.modals.verifyPhoneNumber.title'),
        details: (
          <Stack gap="$6">
            <Text>
              {i18n.t(
                'CreateAccount.content.modals.verifyPhoneNumber.subtitle',
              )}
            </Text>
            <Text>{values.phone}</Text>
            <Text>
              {i18n.t('CreateAccount.content.modals.verifyPhoneNumber.rates')}
            </Text>
          </Stack>
        ),
        primaryAction: {
          label: i18n.t(
            'CreateAccount.content.modals.verifyPhoneNumber.primaryButton.label',
          ),
          onPress: () => {
            mediator.publish('router:navigate', {
              url: '/verify-phone',
              query: {
                ...query,
                phone: values.phone,
              },
            });
            Modal.hide();
          },
        },
        secondaryAction: {
          label: i18n.t(
            'CreateAccount.content.modals.verifyPhoneNumber.secondaryButton.label',
          ),
          onPress: () => {
            Modal.hide();
          },
        },
        buttonLayout: 'row',
      });
    },
    [query],
  );

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    isValid,
    dirty,
    errors,
    touched,
    values,
    isSubmitting,
  } = useFormik({
    initialValues,
    validationSchema: toFormikValidationSchema(validationSchema),
    onSubmit: (formValues: FormValues) => {
      onSubmit({ values: formValues });
    },
    validateOnBlur: true,
  });

  useRunOnceOnMountEffect(
    useCallback(() => {
      // Remove auth session on mount
      removeAuthSession();
    }, []),
  );

  return (
    <MainContainer testID={testID}>
      {isSubmitting && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}

      <InformationalScreen testID={buildTestId({ testID, suffix: 'screen' })}>
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
          topNavigationProps={{
            title: i18n.t('CreateAccount.content.introForm.title'),
            animatedTitle: true,
            buttonStart: {
              label: 'Go Back',
              onPress: handleGoBack,
              element: <TopHeaderBackArrow testID="nav-back" />,
            },
            testID: 'nav-back',
          }}
        >
          <InformationalScreen.Content>
            <Text marginTop={-8}>
              {i18n.t('CreateAccount.content.introForm.subtitle')}
            </Text>
            <FormContainer>
              <PhoneInput
                aria-label={i18n.t(
                  'SignIn.content.signInForm.formField.phone.label',
                )}
                placeholder={i18n.t(
                  'SignIn.content.signInForm.formField.phone.placeholder',
                )}
                label={i18n.t(
                  'SignIn.content.signInForm.formField.phone.label',
                )}
                errorMessage={errors.phone}
                isInvalid={Boolean(errors.phone && touched.phone)}
                value={values.phone}
                testID={buildTestId({ testID, suffix: ['phone', 'input'] })}
                onChangeText={handleChange('phone')}
                onBlur={handleBlur('phone')}
              />
            </FormContainer>
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Footer
              buttonLabel={i18n.t(
                'CreateAccount.content.introForm.footer.primaryButton.label',
              )}
              onButtonPress={handleSubmit}
              disabled={!isValid || !dirty || isSubmitting}
              buttonTestID={buildTestId({
                testID,
                suffix: ['submit', 'button'],
              })}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(CreateAccount);
